import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Button,
} from '@mui/material';
import {
  Assignment as TaskIcon,
  Description as ProcessIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { taskApi, processApi } from '../api/services';
import { useAuthStore } from '../store/authStore';
import { useCryptoKeyStore } from '../features/encryption';

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { activeKey, gkdkSlots, loadGKDKSlots } = useCryptoKeyStore();
  const [gkdkLoadAttempted, setGkdkLoadAttempted] = useState(false);
  const isCitizen = user?.roles.includes('CITIZEN');
  const isAuthority = user?.roles.includes('AUTHORITY');

  // Load GKDK slots on mount to get authority IDs
  useEffect(() => {
    if (activeKey && gkdkSlots.length === 0 && !gkdkLoadAttempted) {
      setGkdkLoadAttempted(true);
      loadGKDKSlots();
    }
  }, [activeKey, gkdkSlots.length, loadGKDKSlots, gkdkLoadAttempted]);

  // Get the first authority ID from GKDK slots (employee's authority)
  const authorityId = gkdkSlots.length > 0 ? gkdkSlots[0].authorityId : undefined;

  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: ['tasks', authorityId],
    queryFn: () => taskApi.getMyTasks(authorityId),
  });

  const { data: processesData, isLoading: processesLoading } = useQuery({
    queryKey: ['processes', authorityId],
    queryFn: () => processApi.getProcesses(false, authorityId),
  });

  const { data: definitions } = useQuery({
    queryKey: ['process-definitions'],
    queryFn: processApi.getDefinitions,
  });

  if (tasksLoading || processesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const tasks = tasksData?.tasks || [];
  const processes = processesData?.processes || [];

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">
          Willkommen, {user?.username}
        </Typography>
        {isCitizen && definitions && definitions.length > 0 && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/processes/new')}
          >
            Neuer Antrag
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TaskIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Offene Aufgaben</Typography>
                <Chip
                  label={tasks.length}
                  size="small"
                  color="primary"
                  sx={{ ml: 'auto' }}
                />
              </Box>
              {tasks.length === 0 ? (
                <Alert severity="info">Keine offenen Aufgaben</Alert>
              ) : (
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {tasks.slice(0, 5).map((task) => (
                    <Box
                      key={task.id}
                      sx={{
                        p: 2,
                        mb: 1,
                        bgcolor: 'action.hover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.selected' },
                      }}
                      onClick={() => navigate(`/tasks/${task.id}`)}
                    >
                      <Typography variant="subtitle2">
                        {task.taskDisplay?.labels['de'] || task.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {task.createTime
                          ? new Date(task.createTime).toLocaleDateString('de-DE')
                          : '-'}
                      </Typography>
                    </Box>
                  ))}
                  {tasks.length > 5 && (
                    <Button
                      fullWidth
                      onClick={() => navigate('/tasks')}
                      sx={{ mt: 1 }}
                    >
                      Alle {tasks.length} Aufgaben anzeigen
                    </Button>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ProcessIcon sx={{ mr: 1, color: 'secondary.main' }} />
                <Typography variant="h6">Aktive Prozesse</Typography>
                <Chip
                  label={processes.length}
                  size="small"
                  color="secondary"
                  sx={{ ml: 'auto' }}
                />
              </Box>
              {processes.length === 0 ? (
                <Alert severity="info">Keine aktiven Prozesse</Alert>
              ) : (
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {processes.slice(0, 5).map((process) => (
                    <Box
                      key={process.id}
                      sx={{
                        p: 2,
                        mb: 1,
                        bgcolor: 'action.hover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.selected' },
                      }}
                      onClick={() => navigate(`/processes/${process.id}`)}
                    >
                      <Typography variant="subtitle2">
                        {process.processDefinitionName || process.processDefinitionKey}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {process.businessKey || process.id.substring(0, 8)}
                      </Typography>
                    </Box>
                  ))}
                  {processes.length > 5 && (
                    <Button
                      fullWidth
                      onClick={() => navigate('/processes')}
                      sx={{ mt: 1 }}
                    >
                      Alle {processes.length} Prozesse anzeigen
                    </Button>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {isAuthority && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Statistik
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h3" color="primary">
                        {tasks.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Zu bearbeitende Aufgaben
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h3" color="secondary">
                        {processes.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Aktive Vorgaenge
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h3" color="success.main">
                        {tasks.filter((t) => t.assignee === user?.username).length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Mir zugewiesen
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
